import NewLogo from "@/assets/v2/svg/new-logo.svg";
import Dashboard from "@/assets/v2/svg/dashboard.svg";
import Roadmap from "@/assets/v2/svg/roadmap.svg";
import MyTasks from "@/assets/v2/svg/my-tasks.svg";
import ProjectTasks from "@/assets/v2/svg/project-tasks.svg";
import ProjectMembers from "@/assets/v2/svg/project-members.svg";
import CirclePlus from "@/assets/v2/svg/add-circle.svg";
import Search from "@/assets/v2/svg/search.svg";
import Close from "@/assets/v2/svg/close-circle.svg";
import Portfolio from "@/assets/v2/svg/portfolio.svg";
import Program from "@/assets/v2/svg/program.svg";
import ProjectIcon from "@/assets/v2/svg/project.svg";
import NewArrow from "@/assets/v2/svg/arrow-down.svg";
import ResourceManagement from "@/assets/v2/svg/resource-management.svg";
import Resources from "@/assets/v2/svg/resources.svg";
import JobTitles from "@/assets/v2/svg/job-titles.svg";
import Absences from "@/assets/v2/svg/absences.svg";
import RiskManagement from "@/assets/v2/svg/risk-management.svg";
import ProjectSettings from "@/assets/v2/svg/project-settings.svg";
// import ProjectBudgetIcon from "@/assets/v2/svg/project-budget.svg";
import ResourceUtilization from "@/assets/v2/svg/resource-utilization.svg";
import OrganisationDetails from "@/assets/v2/svg/organisation-details.svg";
import NonWorkingDays from "@/assets/v2/svg/non-working-days.svg";
import RiskSettings from "@/assets/v2/svg/risk-settings.svg";
import ApiIntegration from "@/assets/v2/svg/api-integration.svg";
import Workload from "../../assets/v2/svg/workload.svg";
import Timesheet from "@/assets/v2/svg/CalendarIcon.svg";
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useUser } from "@/hooks/useUser";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Dialog from "@/components/common/Dialog";
import { TooltipIfTruncated } from "@/components/common/TooltipIfTruncated";
import CreateProjectForm from "../project/CreateProjectForm";
import { Project } from "@/api/query/useProjectQuery";
import { ProjectStatusEnumValue, UserRoleEnumValue } from "@backend/src/schemas/enums";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { usePlanValidation } from "@/hooks/usePlan";
import useSettingsQuery from "@/api/query/useVersionQuery";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";
import { HierarchyItem } from "@/helperFuntions/projectherarch";
import useProjectWithoutCalulationQuery from "@/api/query/useProjectWithoutCalulationQuery";
import Sidebar from "@/assets/v2/svg/sidebar.svg";
import CloseSidebar from "@/assets/v2/svg/collapse-arrow.svg";
import { Loader, Sun, Moon } from "lucide-react";
import { canManageTimesheetApprovals } from "@/utils/projectRoleAccess";
import { useTimesheetSidebarCountsQuery } from "@/api/query/useTimesheetSidebarCountsQuery";

export type SideBarNewProps = {
  isShow: boolean;
  setOpen: (d: boolean) => void;
};

interface SidebarTab {
  Id: number | string;
  Image: any;
  Name: string;
  Path: string;
  plan: boolean;
  subType?: string;
  adminOnly?: boolean;
  children?: SidebarTab[];
  badgeCount?: number;
}

export const SidebarNew = (props: SideBarNewProps) => {
  const { validatePlan } = usePlanValidation();
  const { t } = useTranslation();
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { pathname: location } = useLocation();
  const projectQuery = useProjectWithoutCalulationQuery(!!user);
  const [showProjectMenu, setShowProjectMenu] = useState<string>("");
  const [isPopUpOpen, setIsPopUpOpen] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  const projectData = projectQuery.data?.data.data;
  const [sidebarColor, setSidebarColor] = useState(() => {
    const color = localStorage.getItem("sidebar-color");
    return (color === "purple" || !color) ? "dark-blue" : color;
  });

  // Highlight only these project states in the sidebar folder icon.
  const shouldHighlightProjectFolder = (status?: string) =>
    status === ProjectStatusEnumValue.COMPLETED ||
    status === ProjectStatusEnumValue.ON_HOLD ||
    status === ProjectStatusEnumValue.CANCELLED;

  const getStatusDotColor = (status?: string) => {
    switch (status) {
      case ProjectStatusEnumValue.CANCELLED:
        return "bg-red-500";
      case ProjectStatusEnumValue.ON_HOLD:
        return "bg-amber-500";
      case ProjectStatusEnumValue.COMPLETED:
        return "bg-gray-500";
      default:
        return "";
    }
  };

  const hasElevatedAccess = useMemo(() => {
    const projects = projectData ?? [];
    if (!user) return false;

    const orgRole = user.userOrganisation[0]?.role;
    const isAdmin = orgRole === UserRoleEnumValue.ADMINISTRATOR;
    const isOrgPM = orgRole === UserRoleEnumValue.PROJECT_MANAGER;

    const userAssignments = projects.map((project) =>
      project.assignedUsers.find((au) => au.user.userId === user.userId)
    );

    const isProjectPM = userAssignments.some(
      (au) =>
        au?.projectRole === UserRoleEnumValue.PROJECT_MANAGER ||
        au?.projectRole === UserRoleEnumValue.ADMINISTRATOR
    );

    const isPfPgmManager =
      (user.managedPortfolioIds?.length ?? 0) > 0 ||
      (user.managedProgramIds?.length ?? 0) > 0;

    return isAdmin || isOrgPM || isProjectPM || isPfPgmManager;
  }, [projectData, user]);

  const isPm = useMemo(() => {
    if (hasElevatedAccess) {
      return [1, 2, 3, 4, 5, 6];
    }
    return [1, 2, 5, 6];
  }, [hasElevatedAccess]);

  const canApproveTimesheets = useMemo(
    () => canManageTimesheetApprovals(user),
    [user],
  );

  const timesheetSidebarCountsQuery = useTimesheetSidebarCountsQuery(
    Boolean(user?.userOrganisation?.length),
  );
  const timesheetDraftBadgeCount =
    timesheetSidebarCountsQuery.data?.draftNotSubmittedCount ?? 0;
  const timesheetApprovalBadgeCount =
    timesheetSidebarCountsQuery.data?.pendingApprovalCount ?? 0;

  const formatTimesheetSidebarBadge = (n: number) => (n > 99 ? "99+" : String(n));

  const baseTabVisibility = useMemo(() => {
    const ids = new Set<number | string>(isPm ?? []);
    if (canApproveTimesheets) {
      ids.add("timesheet-group");
      ids.add(6);
      ids.add(7);
    } else {
      ids.add("timesheet-group");
      ids.add(6);
    }
    return ids;
  }, [isPm, canApproveTimesheets]);

  const [openMenus, setOpenMenus] = useState<(number | string)[]>([]);
  const [isSubType, setIsSubType] = useState<string | null>(null);
  const settingQuery = useSettingsQuery();
  const toggleMenu = (id: number | string) => {
    setOpenMenus((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };
  const BaseTabs: SidebarTab[] = [
    {
      Id: 1,
      Image: Dashboard,
      Name: t(`portfolio_dashboard`),
      Path: `/`,
      plan: true, // selectedPlan.features.includes("portfolioDashboard"),
    },
    {
      Id: 2,
      Image: Roadmap,
      Name: t(`project_list`),
      Path: `/projects`,
      plan: true,
    },
    {
      Id: 3,
      Image: ResourceManagement,
      Name: t("resource-management"),
      Path: "",
      plan: true,
      children: [
        {
          Id: 1,
          Image: Resources,
          Name: t("resources"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/organization-member`,
          plan: true,
          subType: "organization-member",
        },
        {
          Id: 2,
          Image: JobTitles,
          Name: t("resource_admin_title"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/resource-administration`,
          plan: true,
          subType: "resource-administration",
        },
        {
          Id: 3,
          Image: Absences,
          Name: t("availability"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/manage-absence`,
          plan: true,
          subType: "manage-absence",
        },
        {
          Id: 4,
          Image: ResourceUtilization,
          Name: t(`Workload Planning`),
          Path: `/resource-management/${user?.userOrganisation[0]?.organisationId}/capacity-planning`,
          plan: true,
          subType: "capacity-planning",
        },
      ],
    },
    {
      Id: 4,
      Image: RiskSettings,
      Name: t(`general_settings`),
      // Path: `/organisation/${user?.userOrganisation[0]?.organisationId}`,
      Path: ``,
      plan: true,
      children: [
        {
          Id: 1,
          Image: OrganisationDetails,
          Name: t("organization-details"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/organization-details`,
          plan: true,
          subType: "organization-details",
        },
        {
          Id: 2,
          Image: NonWorkingDays,
          Name: t("nonworking-days"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/nonworking-days`,
          plan: true,
          subType: "nonworking-days",
        },
        {
          Id: 3,
          Image: RiskSettings,
          Name: t("riskSettings"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/risk-settings`,
          plan: true,
          subType: "risk-settings",
        },
        {
          Id: 4,
          Image: RiskSettings,
          Name: t("kpi_settings"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/kpi-settings`,
          plan: true,
          subType: "kpi-settings",
        },
        {
          Id: 5,
          Image: Timesheet,
          Name: t("timesheet_settings"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/timesheet-settings`,
          plan: true,
          subType: "timesheet-settings",
        },
        {
          Id: 6,
          Image: ApiIntegration,
          Name: t("integrations"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/api-integration`,
          plan: true,
          subType: "api-integration",
        },
        {
          Id: 7,
          Image: ProjectSettings,
          Name: t("project_configuration"),
          Path: `/organisation/${user?.userOrganisation[0]?.organisationId}/project-settings`,
          plan: true,
          subType: "project-settings",
        },
      ],
    },
    {
      Id: 5,
      Image: MyTasks,
      Name: t(`mytask`),
      Path: `/mytasks`,
      plan: true,
    },
    ...(canApproveTimesheets
      ? [
        {
          Id: "timesheet-group",
          Image: Timesheet,
          Name: t("Timesheet"),
          Path: "",
          plan: true,
          children: [
            ...(hasElevatedAccess
              ? [
                {
                  Id: 8,
                  Image: Timesheet,
                  Name: t(`CRA Dashboard`) || "Timesheet Dashboard",
                  Path: `/cra-dashboard`,
                  plan: true,
                },
              ]
              : []),
            {
              Id: 6,
              Image: Timesheet,
              Name: t(`my_timesheet`),
              Path: `/my-timesheet`,
              plan: true,
              badgeCount: timesheetDraftBadgeCount,
            },
            {
              Id: 7,
              Image: Timesheet,
              Name: t(`timesheet_approvals`),
              Path: `/timesheet-approvals`,
              plan: true,
              badgeCount: timesheetApprovalBadgeCount,
            },
          ],
        },
      ]
      : [
        {
          Id: "timesheet-group",
          Image: Timesheet,
          Name: t("Timesheet"),
          Path: "",
          plan: true,
          children: [
            ...(hasElevatedAccess
              ? [
                {
                  Id: 8,
                  Image: Timesheet,
                  Name: t(`CRA Dashboard`) || "Timesheet Dashboard",
                  Path: `/cra-dashboard`,
                  plan: true,
                },
              ]
              : []),
            {
              Id: 6,
              Image: Timesheet,
              Name: t(`my_timesheet`),
              Path: `/my-timesheet`,
              plan: true,
              badgeCount: timesheetDraftBadgeCount,
            },
          ],
        },
      ]),
  ];
  const ProjectTabs = [
    {
      Id: 1,
      Image: Dashboard,
      Name: t(`project_dashboard`),
      Path: `/projectDashboard/`,
      plan: true, //selectedPlan.features.includes("projectDashboard"),
      subType: "projectDashboard",
    },
    {
      Id: 2,
      Image: ProjectTasks,
      Name: t("project_tasks"),
      Path: `/tasks/`,
      plan: true,
      subType: "tasks",
    },
    {
      Id: 3,
      Image: Workload,
      Name: t("project_workload"),
      Path: `/workload/`,
      plan: true,
      subType: "workload",
    },
    {
      Id: 4,
      Image: ProjectMembers,
      Name: t("project_members"),
      Path: `/members/`,
      plan: true,
      subType: "members",
    },
    {
      Id: 5,
      Image: RiskManagement,
      Name: t("Risk Management"),
      Path: `/risk/`,
      plan: true,
      subType: "risk",
    },
    // {
    //   Id: 7,
    //   Image: ProjectBudgetIcon,
    //   Name: t("project_budget"),
    //   Path: `/budget/`,
    //   plan: true,
    //   subType: "budget",
    // },
    {
      Id: 6,
      Image: ProjectSettings,
      Name: t("project_settings"),
      Path: `/project-details/`,
      plan: true,
      subType: "project-details",
    },
  ];
  const handleOpenPopUp = () => {
    if (validatePlan("project").valid) {
      setIsPopUpOpen(!isPopUpOpen);
    }
  };

  const [isMobailView, setIsMobailView] = useState(false);
  useEffect(() => {
    // setIsMobailView(window.outerWidth < 768);
    setIsMobailView(false);
  }, []);

  useEffect(() => {
    const subType = location.split("/")[3] ?? location.split("/")[1];
    if (subType) {
      if (location.split("/")[3]) {
        const Id = BaseTabs.find(
          (item) =>
            item.children && item.children.find((i) => i.subType === subType)
        )?.Id;
        if (!Id) return;
        setOpenMenus([Id]);
        setIsSubType(subType);
      } else {
        setIsSubType(subType);
      }
    }
  }, [location]);

  useEffect(() => {
    if (id || projectId) {
      setShowProjectMenu((id || projectId) ?? "");
      props.setOpen(true);
    }
    if (!id && !projectId) {
      setShowProjectMenu("");
    }
    const sorted = [...(projectQuery.data?.data.data ?? [])].sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    convertToHierarchyData(sorted);
  }, [projectQuery.data?.data.data, id, projectId]);

  const newProjectRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (
      showProjectMenu !== null &&
      newProjectRef.current &&
      (id ?? projectId)
    ) {
      const activeElement =
        newProjectRef?.current[showProjectMenu as unknown as number];
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [id, projectId, showProjectMenu]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [version, setVersion] = useState("");
  const [currentVersion, setCurrentVersion] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [searchProjectName, setSearchProjectName] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setVersion(settingQuery.data?.data.data?.appVersion ?? "");
    setCurrentVersion(localStorage.getItem("VERSION") ?? "");
  }, [settingQuery.data?.data.data]);

  useEffect(() => {
    if (version && settingQuery.isFetched) {
      if (currentVersion !== version) {
        if (!toast.isActive("toast14")) {
          toast.success(
            <div className="lg:flex gap-1 items-center">
              <p className=" text-wrap">
                {t(
                  "A new version of ProjectChef has been released. Please reload the page"
                )}
                <br />
              </p>
              <div className="flex justify-end items-center">
                <button
                  onClick={() => window.location.reload()}
                  className="border-b border-b-white outline-none mt-1 text-xs"
                >
                  {t("reload")}
                </button>
              </div>
            </div>,
            { autoClose: false, toastId: "toast14" }
          );
        }

        // Only update version in localStorage after showing toast
        localStorage.setItem("VERSION", version);
      }
    }
  }, [version, settingQuery.isFetched]);

  type expandedProject = Project & { type: string };
  type ProjectHierarchy = {
    portfolioId?: string;
    portfolioName?: string;
    projects: expandedProject[];
    type: string;
    programs?: program[];
    project?: Project;
  };
  type program = {
    programId: string;
    programName?: string;
    type: string;
    projects: expandedProject[];
  };
  const [convertToHierarchy, setConvertToHierarchy] = useState<any[]>([]);
  const convertToHierarchyData = (projectsList: Project[]) => {
    const hierarchyMap = new Map<string, ProjectHierarchy>();
    const orphanProgramsMap = new Map<string, program>();
    const standaloneProjects: Project[] = [];
    // First Pass - Build Structure
    projectsList.forEach((p: Project) => {
      const hasPortfolio = !!p.portfolio;
      const hasProgram = !!p.program;

      if (!hasPortfolio && !hasProgram) {
        standaloneProjects.push(p);
        return;
      }

      if (!hasPortfolio && hasProgram) {
        if (!orphanProgramsMap.has(p.program!.programId)) {
          orphanProgramsMap.set(p.program!.programId, {
            programId: p.program!.programId,
            programName: p.program!.programName || "",
            type: "program",
            projects: [],
          });
        }
        orphanProgramsMap
          .get(p.program!.programId)
          ?.projects.push({ ...p, type: "project" });
        return;
      }

      if (!hierarchyMap.has(p.portfolio!.portfolioId)) {
        hierarchyMap.set(p.portfolio!.portfolioId, {
          portfolioId: p.portfolio!.portfolioId,
          portfolioName: p.portfolio!.portfolioName || "",
          type: "portfolio",
          projects: [],
          programs: [],
        });
      }

      const portfolioEntry = hierarchyMap.get(p.portfolio!.portfolioId)!;

      if (hasProgram) {
        let programEntry = portfolioEntry.programs?.find(
          (pr) => pr.programId === p.program?.programId
        );

        if (!programEntry) {
          programEntry = {
            programId: p.program!.programId,
            programName: p.program!.programName || "",
            type: "program",
            projects: [],
          };
          portfolioEntry.programs?.push(programEntry);
        }
        programEntry.projects.push({ ...p, type: "project" });
      } else {
        portfolioEntry.projects.push({ ...p, type: "project" });
      }
    });

    // Second Pass - Rebuild Final Hierarchy in Sorted Order
    const sortedHierarchy = projectsList
      .map((p) => {
        if (p.portfolio) return hierarchyMap.get(p.portfolio.portfolioId);
        if (p.program) return orphanProgramsMap.get(p.program.programId);
        return p;
      })
      .filter(Boolean);

    const uniq = [...new Set(sortedHierarchy)];
    setConvertToHierarchy(uniq);
  };

  const filteredHierarchy = useMemo(() => {
    if (!searchProjectName?.trim()) return convertToHierarchy;

    const query = searchProjectName.toLowerCase();

    return convertToHierarchy
      ?.map((item: any) => {
        // 🟦 PORTFOLIO
        if (item.portfolioId) {
          const portfolioMatches = item.portfolioName?.toLowerCase().includes(query);

          const filteredPrograms =
            item.programs
              ?.map((program: any) => {
                const programMatches = program.programName?.toLowerCase().includes(query);
                const filteredProjects = program.projects.filter((p: any) =>
                  p.projectName?.toLowerCase().includes(query) || p.projectCode?.toLowerCase().includes(query)
                );

                const projectsToKeep = (programMatches || portfolioMatches) ? program.projects : filteredProjects;

                return (programMatches || portfolioMatches || filteredProjects.length > 0)
                  ? { ...program, projects: projectsToKeep }
                  : null;
              })
              .filter(Boolean) || [];

          const filteredDirectProjects = item.projects.filter((p: any) =>
            p.projectName?.toLowerCase().includes(query) || p.projectCode?.toLowerCase().includes(query)
          );

          const directProjectsToKeep = portfolioMatches ? item.projects : filteredDirectProjects;

          if (
            !portfolioMatches &&
            filteredPrograms.length === 0 &&
            filteredDirectProjects.length === 0
          ) {
            return null;
          }

          return {
            ...item,
            programs: filteredPrograms,
            projects: directProjectsToKeep,
          };
        }

        // 🟩 ORPHAN PROGRAM
        if (item.programId) {
          const programMatches = item.programName?.toLowerCase().includes(query);
          const filteredProjects = item.projects.filter((p: any) =>
            p.projectName?.toLowerCase().includes(query) || p.projectCode?.toLowerCase().includes(query)
          );

          if (!programMatches && filteredProjects.length === 0) {
            return null;
          }

          return {
            ...item,
            projects: programMatches ? item.projects : filteredProjects,
          };
        }

        // 🟨 STANDALONE PROJECT
        if (item.projectId) {
          return (item.projectName?.toLowerCase().includes(query) || item.projectCode?.toLowerCase().includes(query)) ? item : null;
        }

        return null;
      })
      .filter(Boolean);
  }, [convertToHierarchy, searchProjectName]);

  return (
    <TooltipProvider delayDuration={0}>
      <div
        id="Sidebar"
        className={`bg-gradient-to-b from-[#0e072b] to-[#130b40] border-r border-indigo-950/50 transition-all duration-300 flex-shrink-0 select-none ${isMobailView ? "absolute z-0  w-full h-full " : ""
          } ${props.isShow ? "h-full w-[250px]" : "h-[58px] w-[250px]"}`}
        ref={containerRef}
      >
        <div className="relative w-full h-full">
          {/* {!isMobailView && (
          <div className="absolute -right-4 top-20 z-40 transition-all duration-150">
            <Button
              variant={"primary_outline"}
              className="w-full h-fit p-2 shadow-md"
              onClick={() => props.setOpen(!props.isShow)}
            >
              <img
                src={DoubleLeftArrow}
                alt=""
                className={`h-4 w-4 ${!props.isShow && "rotate-180"}`}
              />
            </Button>
          </div>
        )} */}
          <div
            className={cn(
              "h-full flex flex-col items-center justify-between w-full transition-all duration-150",
              sidebarColor === "light" ? "bg-white text-slate-800 border-r border-slate-200/80" : sidebarColor === "dark-blue" ? "bg-[#0A1128] text-white" : "bg-primary text-white",
              !props.isShow && isMobailView && "!hidden"
            )}
          >
            <div className={`flex w-full h-full ${props.isShow && "flex-col"}`}>
              <div
                className={`flex items-center justify-between gap-3 w-full min-w-0 ${props.isShow ? "p-4" : "p-2"
                  }`}
              >
                <div className="min-w-0 flex-1 overflow-hidden">
                  <img
                    className="h-6 w-auto max-w-full object-contain object-left"
                    style={
                      sidebarColor === "light"
                        ? { filter: "invert(1) hue-rotate(180deg) saturate(2.5)" }
                        : undefined
                    }
                    src={NewLogo}
                    alt="ProjectChef"
                  />
                </div>
                <button
                  type="button"
                  className="flex h-8 w-8 shrink-0 items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 ease-out rounded-lg hover:bg-slate-100/10"
                  onClick={() => props.setOpen(!props.isShow)}
                  aria-label={props.isShow ? "Close sidebar" : "Open sidebar"}
                >
                  <img
                    className={cn(
                      "h-5 w-5 transition-all duration-300 ease-in-out",
                      props.isShow ? "-rotate-90" : "rotate-0",
                      sidebarColor === "light" ? "brightness-0 opacity-60" : "brightness-0 invert"
                    )}
                    src={props.isShow ? CloseSidebar : Sidebar}
                    alt=""
                  />
                </button>
              </div>
              {props.isShow && <div
                className="flex flex-col md:gap-6 xl:gap-6 w-full px-2 grow md:px-5 pb-5 h-full overflow-auto"
                style={{ scrollbarWidth: "none" }}
              >
                {props.isShow && (
                  <div className="w-full">
                    {BaseTabs &&
                      BaseTabs.filter((d) => d.plan)
                        .filter((d) => baseTabVisibility.has(d.Id))
                        .map((item) => {
                          const isDisabled =
                            user && user.userOrganisation.length < 1;
                          const isActive =
                            location.split("/")[1] === item.Path.split("/")[1];
                          const isOpen = openMenus.includes(item.Id);

                          return (
                            <Fragment key={item.Id}>
                              <div
                                className={`${item?.children && item?.children?.length > 0
                                  ? "flex justify-between items-center"
                                  : ""
                                  }`}
                              >
                                <div
                                  className={`flex gap-2 py-2.5 px-3 rounded-lg items-center justify-between w-full transition-all duration-300 hover:scale-[1.02] ${isActive
                                    ? "bg-gradient-to-r from-[#535EF9] to-[#3B82F6] text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.3)] font-bold"
                                    : sidebarColor === "light"
                                      ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                      : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                    } ${isDisabled
                                      ? "cursor-not-allowed opacity-50"
                                      : "cursor-pointer"
                                    }`}
                                  onClick={() => {
                                    if (!isDisabled && item.Path !== "") {
                                      navigate(item.Path);
                                      setOpenMenus([]);
                                      props.setOpen(
                                        isMobailView
                                          ? !props.isShow
                                          : props.isShow
                                      );
                                    } else {
                                      if (item.children && item.children?.length > 0 && !isDisabled) {
                                        toggleMenu(item.Id);
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex gap-2 items-center min-w-0 flex-1">
                                    <div className="md:h-3.5 md:w-3.5 xl:w-5 xl:h-5 shrink-0">
                                      <img
                                        className={cn(
                                          "w-4 h-4 align-middle shrink-0 min-w-4",
                                          isActive ? "invert brightness-0" : sidebarColor === "light" && "brightness-0 opacity-60"
                                        )}
                                        src={item.Image}
                                        alt="No Image Found"
                                      />
                                    </div>
                                    <div className="md:text-sm lg:text-sm font-semibold truncate">
                                      {item.Name}
                                    </div>
                                  </div>
                                  {item.Id === 6 &&
                                    timesheetDraftBadgeCount > 0 && (
                                      <span
                                        className={cn("shrink-0 min-w-[1.25rem] h-6 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center tabular-nums", sidebarColor === "light" ? "bg-slate-100 text-slate-800" : "bg-white/20 text-white")}
                                        aria-hidden
                                      >
                                        {formatTimesheetSidebarBadge(
                                          timesheetDraftBadgeCount,
                                        )}
                                      </span>
                                    )}
                                  {item.Id === 7 &&
                                    timesheetApprovalBadgeCount > 0 && (
                                      <span
                                        className={cn("shrink-0 min-w-[1.25rem] h-6 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center tabular-nums", sidebarColor === "light" ? "bg-slate-100 text-slate-800" : "bg-white/20 text-white")}
                                        aria-hidden
                                      >
                                        {formatTimesheetSidebarBadge(
                                          timesheetApprovalBadgeCount,
                                        )}
                                      </span>
                                    )}
                                </div>

                                {item.children && item.children?.length > 0 && (
                                  <img
                                    src={NewArrow}
                                    className={cn(
                                      "w-3 h-3 transition-transform cursor-pointer",
                                      sidebarColor === "light" ? "brightness-0 opacity-60" : "brightness-0 invert",
                                      isOpen && "rotate-180"
                                    )}
                                    onClick={() => {
                                      if (
                                        !isDisabled &&
                                        item.children &&
                                        item.children.length > 0
                                      ) {
                                        toggleMenu(item.Id);
                                      } else {
                                        if (!isDisabled) {
                                          navigate(item.Path);
                                          props.setOpen(
                                            isMobailView
                                              ? !props.isShow
                                              : props.isShow
                                          );
                                        }
                                      }
                                    }}
                                  />
                                )}
                              </div>
                              <div className="flex ml-4">
                                <div className="w-[1px] bg-gray-50/50 self-stretch shrink-0" />
                                <div className="w-full">

                                  {item.children && isOpen && (
                                    <div className="flex flex-col w-full">
                                      {item.children.map((child: SidebarTab) => {
                                        const isActive = location === child.Path;
                                        return (
                                          <div key={child.Path} className="flex flex-col relative">
                                            <div
                                              className={`flex gap-2 items-center justify-between cursor-pointer px-2 py-1 rounded-lg min-w-0 transition-all duration-300 hover:scale-[1.02] ${isActive
                                                ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold"
                                                : sidebarColor === "light"
                                                  ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                                  : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                                }`}
                                              onClick={() => {
                                                navigate(child.Path);
                                                setIsSubType(
                                                  child.Path.split("/")[3] || ""
                                                );
                                              }}
                                            >
                                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <img
                                                  src={child.Image}
                                                  className={cn(
                                                    "w-3.5 h-3.5 shrink-0 min-w-[14px] align-middle",
                                                    isActive ? "invert brightness-0" : sidebarColor === "light" && "brightness-0 opacity-60"
                                                  )}
                                                  alt="No Image Found"
                                                />
                                                <TooltipIfTruncated
                                                  text={child.Name}
                                                  className="text-xs font-semibold"
                                                />
                                              </div>

                                              {child.badgeCount !== undefined && child.badgeCount > 0 && (
                                                <span
                                                  className={cn("shrink-0 min-w-[1.25rem] h-6 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center tabular-nums ml-2", sidebarColor === "light" ? "bg-slate-100 text-slate-800" : "bg-white/20 text-white")}
                                                  aria-hidden
                                                >
                                                  {formatTimesheetSidebarBadge(child.badgeCount)}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>

                            </Fragment>
                          );
                        })}
                  </div>
                )}
                <hr className={cn("border-dashed", sidebarColor === "light" ? "border-slate-200" : "border-sidebar_text")} />
                {user && user.userOrganisation.length >= 1 && props.isShow && (
                  <div
                    className={cn("w-full h-full relative", sidebarColor === "light" ? "bg-white" : sidebarColor === "dark-blue" ? "bg-[#0A1128]" : "bg-primary")}
                    style={{ scrollbarWidth: "none" }}
                  >
                    <div className={cn("flex justify-between items-center p-2 w-full sticky -top-[1px] z-50", sidebarColor === "light" ? "bg-white" : sidebarColor === "dark-blue" ? "bg-[#0A1128]" : "bg-primary")}>
                      <div className={cn("md:text-xs xl:text-sm font-bold", sidebarColor === "light" ? "text-slate-700" : "text-sidebar_text")}>
                        {t("MY PROJECTS")}
                      </div>
                      <div className="flex gap-3 xl:mr-5 items-center">
                        {!isSearchOpen && (
                          <img
                            className="w-[18px] h-[18px] align-middle cursor-pointer shrink-0 transition-transform duration-200 hover:scale-110"
                            src={Search}
                            alt={t("Search")}
                            title={t("Search Projects")}
                            onClick={() => {
                              setIsSearchOpen(true);
                              setTimeout(() => {
                                searchInputRef.current?.focus();
                              }, 0);
                            }}
                          />
                        )}
                        {(user?.userOrganisation[0]?.role ===
                          UserRoleEnumValue.ADMINISTRATOR ||
                          (user?.managedPortfolioIds &&
                            user.managedPortfolioIds.length > 0) ||
                          (user?.managedProgramIds &&
                            user.managedProgramIds.length > 0)) && (
                            <img
                              className="w-[18px] h-[18px] align-middle cursor-pointer shrink-0 transition-transform duration-200 hover:scale-110"
                              src={CirclePlus}
                              alt={t("Add")}
                              title={t("Create Project")}
                              onClick={handleOpenPopUp}
                            />
                          )}
                      </div>
                    </div>

                    <div className=" md:mt-1.5 w-full" key={`${projectId} ${id}`}>
                      {isSearchOpen && (
                        <div className="relative w-full mb-2">
                          <input
                            type="text"
                            ref={searchInputRef}
                            className={cn("w-full p-2 pr-8 focus-visible:border-none rounded-md text-sm", sidebarColor === "light" ? "bg-slate-100 placeholder:text-slate-400 text-slate-800" : "bg-[#57517A] placeholder:text-sidebar_text text-white")}
                            id="projectName"
                            value={searchProjectName}
                            onChange={(e) => setSearchProjectName(e.target.value)}
                            placeholder={t("Search Projects here...")}
                          />
                          <img
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-[18px] h-[18px] cursor-pointer opacity-70 hover:opacity-100 transition-all duration-200 hover:scale-110"
                            src={Close}
                            alt={t("Close")}
                            title={t("Close Search")}
                            onClick={() => {
                              setIsSearchOpen(false);
                              setSearchProjectName("");
                            }}
                          />
                        </div>
                      )}
                      {filteredHierarchy?.length === 0 && (
                        <div className="flex justify-center items-center h-full text-sidebar_text mt-5">
                          {t("No projects found")}
                        </div>
                      )}
                      {
                        projectQuery.isRefetching && (
                          <div className="w-full justify-center flex">
                            <Loader className="animate-spin" />
                          </div>
                        )
                      }
                      {projectQuery &&
                        filteredHierarchy?.map((item: any) => {
                          if (item && item?.portfolioId) {
                            return (
                              <CollapsibleItem
                                key={item.portfolioId}
                                label={item?.portfolioName}
                                type={"portfolio"}
                                item={item}
                                showProjectMenu={showProjectMenu}
                                autoExpand={!!searchProjectName?.trim()}
                              >
                                {item.programs.map((program: any) => (
                                  <CollapsibleItem
                                    key={program.programId}
                                    label={program.programName}
                                    type="program"
                                    indentLevel={1}
                                    item={item}
                                    showProjectMenu={showProjectMenu}
                                    autoExpand={!!searchProjectName?.trim()}
                                  >
                                    {program.projects.map((item: any) => {
                                      if (item && item?.projectId) {
                                        return (
                                          <div className="w-full" key={item.projectId}>
                                            <div
                                              className={`flex justify-between items-center mt-2 w-full p-2.5 rounded-lg transition-all duration-300 hover:scale-[1.02] group ${item?.projectId === (projectId ?? id)
                                                ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                                : sidebarColor === "light"
                                                  ? "bg-slate-100/60 text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/40"
                                                  : (sidebarColor === "dark-blue" ? "bg-[#0A1128]/40 text-sidebar_text hover:bg-white/5 hover:text-white" : "bg-primary/40 text-sidebar_text hover:bg-white/5 hover:text-white")
                                                } ${item.projectId == showProjectMenu && "focus"
                                                } cursor-pointer ${!item?.isActive
                                                  ? "opacity-70 bg-gray-400/30 cursor-not-allowed"
                                                  : ""
                                                }`}
                                              onClick={() => {
                                                if (item?.isActive) {
                                                  setShowProjectMenu(
                                                    item.projectId ==
                                                      showProjectMenu
                                                      ? ""
                                                      : item.projectId
                                                  );
                                                } else {
                                                  setShowUpgradeModal(true);
                                                }
                                              }}
                                              autoFocus={
                                                item.projectId == showProjectMenu
                                              }
                                            >
                                              <div className="md:text-sm xl:text-sm flex items-center justify-between w-full min-w-0">
                                                <div className="flex w-11/12 gap-2 min-w-0">
                                                  <Tooltip>
                                                    <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                      <div className="relative w-4 h-4 min-w-4 flex-shrink-0">
                                                        <img
                                                          src={ProjectIcon}
                                                          alt="No Project Found"
                                                          className={cn(
                                                            "w-4 h-4 min-w-4 transition-all duration-200",
                                                            item.projectId === (projectId ?? id)
                                                              ? "invert brightness-0"
                                                              : sidebarColor === "light"
                                                                ? "brightness-0 opacity-60 group-hover:opacity-100"
                                                                : "opacity-80 group-hover:opacity-100"
                                                          )}
                                                        />
                                                        {shouldHighlightProjectFolder(item.status) && (
                                                          <span className={cn(
                                                            "absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full border border-white",
                                                            getStatusDotColor(item.status)
                                                          )} />
                                                        )}
                                                      </div>
                                                    </TooltipTrigger>
                                                    {shouldHighlightProjectFolder(item.status) && (
                                                      <TooltipContent>
                                                        <p className="text-sm text-center">
                                                          {t(String(item.status)) || item.status}
                                                        </p>
                                                      </TooltipContent>
                                                    )}
                                                  </Tooltip>
                                                  <TooltipIfTruncated
                                                    text={item?.projectName}
                                                    className={cn(
                                                      "text-sm text-left break-normal font-semibold min-w-0 transition-colors duration-200",
                                                      item.projectId === (projectId ?? id)
                                                        ? "text-white font-bold"
                                                        : sidebarColor === "light"
                                                          ? "text-slate-600 group-hover:text-slate-900"
                                                          : "text-sidebar_text group-hover:text-white"
                                                    )}
                                                  />
                                                </div>
                                                <img
                                                  className={cn(
                                                    "w-3 h-3 transition-all duration-200",
                                                    item.projectId === (projectId ?? id)
                                                      ? "invert brightness-0"
                                                      : sidebarColor === "light"
                                                        ? "brightness-0 opacity-60 group-hover:opacity-100"
                                                        : "opacity-60 group-hover:opacity-100",
                                                    showProjectMenu === item.projectId && "rotate-180"
                                                  )}
                                                  src={NewArrow}
                                                  alt="No Image Found"
                                                />
                                              </div>
                                            </div>
                                            <div
                                              className={` transition flex w-full duration-200${showProjectMenu === item.projectId
                                                ? "translate-y-0 "
                                                : "-translate-y-full -z-10 opacity-0 absolute"
                                                }`}
                                            >
                                              <div className="w-[1px] bg-gray-50/50 ml-2 self-stretch shrink-0" />
                                              <div className="min-w-0 w-full flex-1 flex flex-col">
                                                {ProjectTabs &&
                                                  ProjectTabs.filter(
                                                    (d) => d.plan
                                                  ).map((projectitem) => {
                                                    return (
                                                      <div
                                                        key={projectitem.subType}
                                                        className={`px-2 py-1 flex gap-2 mb-1 items-center mt-0.5 cursor-pointer transition-all duration-300 hover:scale-[1.02] rounded-lg group ${item.projectId == (id ?? projectId) &&
                                                          location.split("/")[1] == projectitem.Path.split("/")[1] &&
                                                          projectitem.subType === isSubType
                                                          ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                                          : sidebarColor === "light"
                                                            ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                                            : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                                          } `}
                                                        onClick={() => {
                                                          props.setOpen(
                                                            isMobailView
                                                              ? !props.isShow
                                                              : props.isShow
                                                          );
                                                          if (
                                                            projectitem.Name ===
                                                            t("project_workload")
                                                          ) {
                                                            setIsSubType("workload");
                                                            navigate(
                                                              `${projectitem.Path}${item.projectId}?view=GANTT`,
                                                              {
                                                                state: {
                                                                  isGanttOpen: true,
                                                                },
                                                              }
                                                            );
                                                          } else {
                                                            navigate(
                                                              `${projectitem.Path}${item.projectId}`
                                                            );
                                                            setIsSubType(
                                                              projectitem.Path.split(
                                                                "/"
                                                              )[1]
                                                            );
                                                          }
                                                        }}
                                                      >
                                                        <img
                                                          className={`align-middle w-3.5 h-3.5 min-w-[14px] ${item.projectId === (projectId ?? id) && projectitem.subType === isSubType ? "invert brightness-0" : ""}`}
                                                          src={projectitem.Image}
                                                          alt="No Image Found"
                                                        />
                                                        {/* </div> */}
                                                        <TooltipIfTruncated
                                                          text={projectitem.Name}
                                                          className={cn(
                                                            "text-xs font-semibold",
                                                            item.projectId === (projectId ?? id) &&
                                                              projectitem.subType === isSubType
                                                              ? "text-white"
                                                              : sidebarColor === "light"
                                                                ? "text-slate-600 group-hover:text-slate-900"
                                                                : "text-sidebar_text group-hover:text-white"
                                                          )}
                                                        />
                                                      </div>
                                                    );
                                                  })}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                    })}
                                  </CollapsibleItem>
                                ))}

                                {/* Projects directly under portfolio */}
                                {item?.projects.map((item: any) => {
                                  if (item && item?.projectId) {
                                    return (
                                      <div key={item.projectId}>
                                        <div
                                          className={`flex justify-between items-center gap-2 mt-2 w-full p-2 rounded-lg transition duration-150 group ${item?.projectId === (projectId ?? id)
                                            ? "bg-secondary"
                                            : (sidebarColor === "light" ? "bg-slate-100/60 text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/40" : (sidebarColor === "dark-blue" ? "bg-[#0A1128]/40" : "bg-primary/40"))
                                            } ${item.projectId == showProjectMenu &&
                                            "focus"
                                            } cursor-pointer ${!item?.isActive
                                              ? "opacity-70 bg-gray-400/30 cursor-not-allowed"
                                              : ""
                                            }`}
                                          onClick={() => {
                                            if (item?.isActive) {
                                              setShowProjectMenu(
                                                item.projectId ==
                                                  showProjectMenu
                                                  ? ""
                                                  : item.projectId
                                              );
                                            } else {
                                              setShowUpgradeModal(true);
                                            }
                                          }}
                                          autoFocus={
                                            item.projectId == showProjectMenu
                                          }
                                        >
                                          <div className="md:text-sm xl:text-sm flex gap-2 items-center w-full min-w-0">
                                            <div className="flex gap-2 items-center w-11/12 min-w-0">
                                              {/* <div className="h-5 min-h-5"> */}
                                              {/* <Package size={18} color="#fff" /> */}
                                              <Tooltip>
                                                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                  <div className="relative w-4 h-4 min-w-4 flex-shrink-0">
                                                    <img
                                                      src={ProjectIcon}
                                                      alt="No Project Found"
                                                      className={cn(
                                                        "w-4 h-4",
                                                        item.projectId === (projectId ?? id) && "invert brightness-0"
                                                      )}
                                                    />
                                                    {shouldHighlightProjectFolder(item.status) && (
                                                      <span className={cn(
                                                        "absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full border border-white",
                                                        getStatusDotColor(item.status)
                                                      )} />
                                                    )}
                                                  </div>
                                                </TooltipTrigger>
                                                {shouldHighlightProjectFolder(item.status) && (
                                                  <TooltipContent>
                                                    <p className="text-sm text-center">
                                                      {t(String(item.status)) || item.status}
                                                    </p>
                                                  </TooltipContent>
                                                )}
                                              </Tooltip>
                                              <TooltipIfTruncated
                                                text={item?.projectName}
                                                className={cn(
                                                  "text-sm text-left break-normal md:max-w-36 xl:max-w-52 font-semibold",
                                                  item.projectId === (projectId ?? id)
                                                    ? "text-white"
                                                    : sidebarColor === "light" ? "text-slate-700 group-hover:text-slate-900" : "text-sidebar_text group-hover:text-white"
                                                )}
                                              />
                                              <div className="w-4 h-4 p-2 relative  rounded-full flex justify-center items-center">
                                                <img
                                                  className={cn("w-3 h-3 top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 absolute transition-transform", item.projectId === (projectId ?? id) || sidebarColor !== "light" ? "brightness-0 invert" : "brightness-0 opacity-60", showProjectMenu === item.projectId && "rotate-180")}
                                                  src={NewArrow}
                                                  alt="No Image Found"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div
                                          className={` transition flex w-full duration-200${showProjectMenu === item.projectId
                                            ? "translate-y-0 "
                                            : "-translate-y-full -z-10 opacity-0 absolute"
                                            }`}
                                        >
                                          <div className="w-[1px] bg-gray-50/50 ml-2 self-stretch shrink-0" />
                                          <div className="min-w-0 w-full flex-1 flex flex-col">
                                            {ProjectTabs &&
                                              ProjectTabs.filter((d) => d.plan).map(
                                                (projectitem) => {
                                                  return (
                                                    <div
                                                      key={projectitem.subType}
                                                      ref={(el) =>
                                                      (newProjectRef.current[
                                                        item.projectId
                                                      ] = el)
                                                      }
                                                      className={`px-2 py-1 flex gap-2 mb-1 items-center mt-0.5 cursor-pointer transition-all duration-300 hover:scale-[1.02] rounded-lg group ${item.projectId == (id ?? projectId) &&
                                                        location.split("/")[1] == projectitem.Path.split("/")[1] &&
                                                        projectitem.subType === isSubType
                                                        ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                                        : sidebarColor === "light"
                                                          ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                                          : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                                        } `}
                                                      onClick={() => {
                                                        props.setOpen(
                                                          isMobailView
                                                            ? !props.isShow
                                                            : props.isShow
                                                        );
                                                        if (
                                                          projectitem.Name ===
                                                          t("project_workload")
                                                        ) {
                                                          setIsSubType("workload");
                                                          navigate(
                                                            `${projectitem.Path}${item.projectId}?view=GANTT`,
                                                            {
                                                              state: {
                                                                isGanttOpen: true,
                                                              },
                                                            }
                                                          );
                                                        } else {
                                                          navigate(
                                                            `${projectitem.Path}${item.projectId}`
                                                          );
                                                          setIsSubType(
                                                            projectitem.Path.split(
                                                              "/"
                                                            )[1]
                                                          );
                                                        }
                                                      }}
                                                    >
                                                      {/* <div className="md:w-3.5 md:h-3.5 xl:w-5 xl:h-5"> */}
                                                      <img
                                                        className={`align-middle w-3.5 h-3.5 min-w-[14px] ${item.projectId ===
                                                          (projectId ?? id) &&
                                                          projectitem.subType ===
                                                          isSubType &&
                                                          "invert brightness-0"
                                                          }`}
                                                        src={projectitem.Image}
                                                        alt="No Image Found"
                                                      />
                                                      {/* </div> */}
                                                      <TooltipIfTruncated
                                                        text={projectitem.Name}
                                                        className={cn(
                                                          "text-xs font-semibold",
                                                          item.projectId === (projectId ?? id) &&
                                                            projectitem.subType === isSubType
                                                            ? "text-white"
                                                            : sidebarColor === "light"
                                                              ? "text-slate-600 group-hover:text-slate-900"
                                                              : "text-sidebar_text group-hover:text-white"
                                                        )}
                                                      />
                                                    </div>
                                                  );
                                                }
                                              )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </CollapsibleItem>
                            );
                          }
                          if (item && item?.programId) {
                            return (
                              <CollapsibleItem
                                key={item.programId}
                                label={item?.programName}
                                type={"program"}
                                item={item}
                                showProjectMenu={showProjectMenu}
                                autoExpand={!!searchProjectName?.trim()}
                              >
                                {item?.projects.map((item: any) => {
                                  if (item && item?.projectId) {
                                    return (
                                      <div key={item.projectId}>
                                        <div
                                          className={`flex justify-between items-center gap-2 mt-2 w-full p-2 rounded-lg transition duration-150 group ${item?.projectId === (projectId ?? id)
                                            ? "bg-secondary"
                                            : (sidebarColor === "light" ? "bg-slate-100/60 text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/40" : (sidebarColor === "dark-blue" ? "bg-[#0A1128]/40" : "bg-primary/40"))
                                            } ${item.projectId == showProjectMenu &&
                                            "focus"
                                            } cursor-pointer ${!item?.isActive
                                              ? "opacity-70 bg-gray-400/30 cursor-not-allowed"
                                              : ""
                                            }`}
                                          onClick={() => {
                                            item?.isActive
                                              ? setShowProjectMenu(
                                                item.projectId ==
                                                  showProjectMenu
                                                  ? ""
                                                  : item.projectId
                                              )
                                              : "";
                                          }}
                                          autoFocus={
                                            item.projectId == showProjectMenu
                                          }
                                        >
                                          <div className="md:text-sm xl:text-sm flex gap-2 items-center w-full min-w-0">
                                            <div className="flex gap-2 items-center w-11/12 min-w-0">
                                              <Tooltip>
                                                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                  <div className="relative w-4 h-4 min-w-4 flex-shrink-0">
                                                    <img
                                                      src={ProjectIcon}
                                                      alt="No Project Found"
                                                      className={cn(
                                                        "w-4 h-4",
                                                        item.projectId === (projectId ?? id) && "invert brightness-0"
                                                      )}
                                                    />
                                                    {shouldHighlightProjectFolder(item.status) && (
                                                      <span className={cn(
                                                        "absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full border border-white",
                                                        getStatusDotColor(item.status)
                                                      )} />
                                                    )}
                                                  </div>
                                                </TooltipTrigger>
                                                {shouldHighlightProjectFolder(item.status) && (
                                                  <TooltipContent>
                                                    <p className="text-sm text-center">
                                                      {t(String(item.status)) || item.status}
                                                    </p>
                                                  </TooltipContent>
                                                )}
                                              </Tooltip>
                                              <TooltipIfTruncated
                                                text={item?.projectName}
                                                className={cn(
                                                  "text-sm text-left break-normal font-semibold min-w-0",
                                                  item.projectId === (projectId ?? id)
                                                    ? "text-white"
                                                    : sidebarColor === "light" ? "text-slate-700" : "text-sidebar_text"
                                                )}
                                              />
                                            </div>
                                            <img
                                              className={cn(
                                                "w-3 h-3 transition-transform",
                                                item.projectId === (projectId ?? id) || sidebarColor !== "light" ? "brightness-0 invert" : "brightness-0 opacity-60",
                                                showProjectMenu === item.projectId && "rotate-180"
                                              )}
                                              src={NewArrow}
                                              alt="No Image Found"
                                            />
                                          </div>
                                        </div>
                                        <div
                                          className={`transition flex w-full duration-200 ${showProjectMenu === item.projectId
                                            ? "translate-y-0 "
                                            : "-translate-y-full -z-10 opacity-0 absolute"
                                            }`}
                                        >
                                          <div className="w-[1px] bg-gray-50/50 ml-2 self-stretch shrink-0" />
                                          <div className="min-w-0 w-full flex-1 flex flex-col">
                                            {ProjectTabs &&
                                              ProjectTabs.filter(
                                                (d) => d.plan
                                              ).map((projectitem) => {
                                                return (
                                                  <div
                                                    key={projectitem.subType}
                                                    className={`px-2 py-1 flex gap-2 mb-1 items-center mt-0.5 cursor-pointer transition-all duration-300 hover:scale-[1.02] rounded-lg group ${item.projectId == (id ?? projectId) &&
                                                      location.split("/")[1] == projectitem.Path.split("/")[1] &&
                                                      projectitem.subType === isSubType
                                                      ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                                      : sidebarColor === "light"
                                                        ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                                        : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                                      } `}
                                                    onClick={() => {
                                                      props.setOpen(
                                                        isMobailView
                                                          ? !props.isShow
                                                          : props.isShow
                                                      );
                                                      if (
                                                        projectitem.Name ===
                                                        t("project_workload")
                                                      ) {
                                                        setIsSubType("workload");
                                                        navigate(
                                                          `${projectitem.Path}${item.projectId}?view=GANTT`,
                                                          {
                                                            state: {
                                                              isGanttOpen: true,
                                                            },
                                                          }
                                                        );
                                                      } else {
                                                        navigate(
                                                          `${projectitem.Path}${item.projectId}`
                                                        );
                                                        setIsSubType(
                                                          projectitem.Path.split(
                                                            "/"
                                                          )[1]
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    <img
                                                      className={`align-middle w-3.5 h-3.5 min-w-[14px] ${item.projectId === (projectId ?? id) && projectitem.subType === isSubType ? "invert brightness-0" : ""}`}
                                                      src={projectitem.Image}
                                                      alt="No Image Found"
                                                    />
                                                    <TooltipIfTruncated
                                                      text={projectitem.Name}
                                                      className={cn(
                                                        "text-xs font-semibold",
                                                        item.projectId === (projectId ?? id) &&
                                                          projectitem.subType === isSubType
                                                          ? "text-white"
                                                          : sidebarColor === "light"
                                                            ? "text-slate-600 group-hover:text-slate-900"
                                                            : "text-sidebar_text group-hover:text-white"
                                                      )}
                                                    />
                                                  </div>
                                                );
                                              })}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }
                                })}
                              </CollapsibleItem>
                            );
                          }
                          if (item && item?.projectId) {
                            return (
                              <div className="w-full" key={item.projectId}>
                                <div
                                  className={`flex justify-between items-center mt-2 w-full p-2.5 rounded-lg transition-all duration-300 hover:scale-[1.02] group ${item?.projectId === (projectId ?? id)
                                    ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                    : sidebarColor === "light"
                                      ? "bg-slate-100/60 text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200/40"
                                      : (sidebarColor === "dark-blue" ? "bg-[#0A1128]/40 text-sidebar_text hover:bg-white/5 hover:text-white" : "bg-primary/40 text-sidebar_text hover:bg-white/5 hover:text-white")
                                    } ${item.projectId == showProjectMenu && "focus"
                                    } cursor-pointer ${!item?.isActive
                                      ? "opacity-70 bg-gray-400/30 cursor-not-allowed"
                                      : ""
                                    }`}
                                  onClick={() => {
                                    if (item?.isActive) {
                                      setShowProjectMenu(
                                        item.projectId == showProjectMenu
                                          ? ""
                                          : item.projectId
                                      );
                                    } else {
                                      setShowUpgradeModal(true);
                                    }
                                  }}
                                  autoFocus={item.projectId == showProjectMenu}
                                >
                                  <div className="md:text-sm xl:text-sm flex items-center justify-between w-full min-w-0">
                                    <div className="flex w-11/12 gap-2 min-w-0">
                                      <Tooltip>
                                        <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                                          <div className="relative w-4 h-4 min-w-4 flex-shrink-0">
                                            <img
                                              src={ProjectIcon}
                                              alt="No Project Found"
                                              className={cn(
                                                "w-4 h-4 min-w-4 transition-all duration-200",
                                                item.projectId === (projectId ?? id)
                                                  ? "invert brightness-0"
                                                  : sidebarColor === "light"
                                                    ? "brightness-0 opacity-60 group-hover:opacity-100"
                                                    : "opacity-80 group-hover:opacity-100"
                                              )}
                                            />
                                            {shouldHighlightProjectFolder(item.status) && (
                                              <span className={cn(
                                                "absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full border border-white",
                                                getStatusDotColor(item.status)
                                              )} />
                                            )}
                                          </div>
                                        </TooltipTrigger>
                                        {shouldHighlightProjectFolder(item.status) && (
                                          <TooltipContent>
                                            <p className="text-sm text-center">
                                              {t(String(item.status)) || item.status}
                                            </p>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                      <TooltipIfTruncated
                                        text={item?.projectName}
                                        className={cn(
                                          "text-sm text-left break-normal font-semibold min-w-0 transition-colors duration-200",
                                          item.projectId === (projectId ?? id)
                                            ? "text-white font-bold"
                                            : sidebarColor === "light"
                                              ? "text-slate-600 group-hover:text-slate-900"
                                              : "text-sidebar_text group-hover:text-white"
                                        )}
                                      />
                                    </div>
                                    <img
                                      className={cn(
                                        "w-3 h-3 transition-all duration-200",
                                        item.projectId === (projectId ?? id)
                                          ? "invert brightness-0"
                                          : sidebarColor === "light"
                                            ? "brightness-0 opacity-60 group-hover:opacity-100"
                                            : "opacity-60 group-hover:opacity-100",
                                        showProjectMenu === item.projectId && "rotate-180"
                                      )}
                                      src={NewArrow}
                                      alt="No Image Found"
                                    />
                                  </div>
                                </div>
                                <div
                                  className={`transition flex w-full duration-200 ${showProjectMenu === item.projectId
                                    ? "translate-y-0 "
                                    : "-translate-y-full -z-10 opacity-0 absolute"
                                    }`}
                                >
                                  <div className="w-[1px] bg-gray-50/50 ml-2 self-stretch shrink-0" />
                                  <div className="min-w-0 w-full flex-1 flex flex-col">
                                    {ProjectTabs &&
                                      ProjectTabs.filter((d) => d.plan).map(
                                        (projectitem) => {
                                          return (
                                            <div
                                              key={projectitem.subType}
                                              ref={(el) =>
                                              (newProjectRef.current[
                                                item.projectId
                                              ] = el)
                                              }
                                              className={`px-2 py-1 flex gap-2 mb-1 items-center mt-0.5 cursor-pointer transition-all duration-300 hover:scale-[1.02] rounded-lg group ${item.projectId == (id ?? projectId) &&
                                                location.split("/")[1] == projectitem.Path.split("/")[1] &&
                                                projectitem.subType === isSubType
                                                ? "bg-gradient-to-r from-[#535EF9]/90 to-[#3B82F6]/90 text-white shadow-[0_4px_12px_-2px_rgba(83,94,249,0.25)] font-bold animate-fade-in"
                                                : sidebarColor === "light"
                                                  ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                                  : "text-sidebar_text hover:bg-white/5 hover:text-white"
                                                } `}
                                              onClick={() => {
                                                props.setOpen(
                                                  isMobailView
                                                    ? !props.isShow
                                                    : props.isShow
                                                );
                                                if (
                                                  projectitem.Name ===
                                                  t("project_workload")
                                                ) {
                                                  setIsSubType("workload");
                                                  navigate(
                                                    `${projectitem.Path}${item.projectId}?view=GANTT`,
                                                    { state: { isGanttOpen: true } }
                                                  );
                                                } else {
                                                  navigate(
                                                    `${projectitem.Path}${item.projectId}`
                                                  );
                                                  setIsSubType(
                                                    projectitem.Path.split("/")[1]
                                                  );
                                                }
                                              }}
                                            >
                                              <img
                                                className={`align-middle w-3.5 h-3.5 min-w-[14px] ${item.projectId ===
                                                  (projectId ?? id) &&
                                                  projectitem.subType ===
                                                  isSubType &&
                                                  "invert brightness-0"
                                                  }`}
                                                src={projectitem.Image}
                                                alt="No Image Found"
                                              />
                                              <TooltipIfTruncated
                                                text={projectitem.Name}
                                                className={cn(
                                                  "text-xs font-semibold",
                                                  item.projectId === (projectId ?? id) &&
                                                    projectitem.subType === isSubType
                                                    ? "text-white"
                                                    : sidebarColor === "light"
                                                      ? "text-slate-600 group-hover:text-slate-900"
                                                      : "text-sidebar_text group-hover:text-white"
                                                )}
                                              />
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>

                                </div>
                              </div>
                            );
                          }
                        })}
                    </div>
                  </div>
                )}
              </div>}
              {/* <div>
              {props.isShow && (
                <div className="w-full pb-5 p-5 mt-auto">
                  <button
                    type="button"
                    className="w-full bg-primary flex items-center gap-3 px-5 py-3 rounded-md md:text-sm xl:text-base"
                    onClick={logout}
                  >
                    <div className="md:w-3.5 md:h-3.5 xl:w-5 xl:h-5">
                      <img
                        className="w-full align-middle"
                        src={NewLogout}
                        alt="No Image Found"
                      />
                    </div>
                    {t("logout")}
                  </button>
                </div>
              )}
              </div> */}
              {props.isShow && (
                <div className="mx-5 mb-3 flex items-center justify-start">
                  <button
                    onClick={() => {
                      const newColor = sidebarColor === "light" ? "dark-blue" : "light";
                      setSidebarColor(newColor);
                      localStorage.setItem("sidebar-color", newColor);
                      window.dispatchEvent(new Event("sidebar-theme-changed"));
                    }}
                    className={cn(
                      "p-2 rounded-lg border transition-all cursor-pointer",
                      sidebarColor === "light"
                        ? "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                        : "bg-white/5 border-white/10 text-sidebar_text hover:text-white hover:bg-white/10"
                    )}
                    title={sidebarColor === "light" ? t("Dark Mode") : t("Light Mode")}
                  >
                    {sidebarColor === "light" ? (
                      <Moon className="w-4 h-4" />
                    ) : (
                      <Sun className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )}
              {version && (
                <div className="ml-5 mb-2">
                  <span className="text-[10px]">{version}</span>
                </div>
              )}
            </div>
          </div>
          {isPopUpOpen && (
            <CreateProjectForm handleClosePopUp={handleOpenPopUp} />
          )}
          {showUpgradeModal && (
            <Dialog
              isOpen={showUpgradeModal}
              onClose={() => setShowUpgradeModal(false)}
              modalClass="rounded-lg "
              bgClass="!backdrop-blur-sm"
            >
              <div className="flex flex-col gap-4 p-6 w-96 text-center text-gray-955 font-medium">
                {(!user?.userOrganisation[0]?.role ||
                  user?.userOrganisation[0]?.role ==
                  UserRoleEnumValue.COLLABORATOR) ? (
                  t("You do not have access to this dashboard with your current plan. Please contact your administrator to upgrade the current plan")
                ) : (
                  t("Project dashboard are not available in this plan. Do you want to upgrade your plan?")
                )}
                <div className="flex gap-2 ml-auto">
                  {(!user?.userOrganisation[0]?.role ||
                    user?.userOrganisation[0]?.role ==
                    UserRoleEnumValue.COLLABORATOR) ? (
                    <Button
                      variant={"outline"}
                      onClick={() => {
                        setShowUpgradeModal(false);
                      }}
                    >
                      {t("ok")}
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={"outline"}
                        onClick={() => {
                          setShowUpgradeModal(false);
                        }}
                      >
                        {t("cancel")}
                      </Button>
                      <Button
                        variant={"primary"}
                        className="text-white"
                        onClick={() => {
                          setShowUpgradeModal(false);
                          navigate("/plans");
                        }}
                      >
                        {t("Upgrade")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Dialog>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

const CollapsibleItem = ({
  label,
  children,
  type,
  showProjectMenu,
  item,
  autoExpand,
}: {
  label: string;
  showProjectMenu: string;
  children: React.ReactNode;
  type: "portfolio" | "program";
  indentLevel?: number;
  item: any;
  autoExpand?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };
  const isExpanding = (item: HierarchyItem): boolean => {
    if (autoExpand) return true;
    if (item.type === "project") {
      return item.projectId === showProjectMenu;
    }

    if (item.type === "program") {
      return item.projects.some((p) => p.projectId === showProjectMenu);
    }

    if (item.type === "portfolio") {
      return (
        item.projects.some((p) => p.projectId === showProjectMenu) ||
        item.programs.some((program) =>
          program.projects.some((p) => p.projectId === showProjectMenu)
        )
      );
    }

    return false;
  };

  useEffect(() => {
    setIsExpanded(isExpanding(item));
  }, [item, autoExpand]);

  useEffect(() => {
    if (isExpanded) {
      if (childrenRef) {
        childrenRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }, [isExpanded]);
  const childrenRef = useRef<HTMLDivElement>(null);
  const sidebarColor = localStorage.getItem("sidebar-color") || "purple";
  return (
    <div className="w-full">
      <div
        className={cn(
          "w-full gap-2 mt-2 p-2 justify-between flex items-center rounded-md transition duration-150 cursor-pointer group",
          sidebarColor === "light"
            ? "bg-slate-100/50 hover:bg-slate-100 border border-slate-200/40"
            : sidebarColor === "dark-blue"
              ? "bg-[#0A1128]/40 hover:bg-white/5"
              : "bg-white/5 hover:bg-white/10"
        )}
        onClick={toggleExpand}
      >

        <div className="flex gap-2 items-center w-11/12 min-w-0 justify-start">
          {" "}
          <div className="w-4 min-w-4 shrink-0">
            {type == "portfolio" ? (
              <img
                className={cn(
                  "transition-all duration-250",
                  sidebarColor === "light" ? "brightness-0 opacity-60 group-hover:opacity-100" : "opacity-80 group-hover:opacity-100"
                )}
                src={Portfolio}
                alt="No Portfolio Found"
              />
            ) : (
              <img
                className={cn(
                  "transition-all duration-250",
                  sidebarColor === "light" ? "brightness-0 opacity-60 group-hover:opacity-100" : "opacity-80 group-hover:opacity-100"
                )}
                src={Program}
                alt="No Program Found"
              />
            )}{" "}
          </div>
          <TooltipIfTruncated
            text={label}
            className={cn(
              "font-semibold text-sm min-w-0 transition-colors duration-250",
              sidebarColor === "light"
                ? "text-slate-700 group-hover:text-slate-900"
                : "text-sidebar_text group-hover:text-white"
            )}
          />
        </div>
        {isExpanded ? (
          <img
            className={cn(
              "w-3 h-3 rotate-180 transition-all duration-250",
              sidebarColor === "light"
                ? "brightness-0 opacity-60 group-hover:opacity-100"
                : "brightness-0 invert opacity-60 group-hover:opacity-100"
            )}
            src={NewArrow}
            alt="No Image Found"
          />
        ) : (
          <img
            className={cn(
              "w-3 h-3 transition-all duration-250",
              sidebarColor === "light"
                ? "brightness-0 opacity-60 group-hover:opacity-100"
                : "brightness-0 invert opacity-60 group-hover:opacity-100"
            )}
            src={NewArrow}
            alt="No Image Found"
          />
        )}
      </div>

      {
        <div className={cn("flex ml-4", isExpanded ? "" : "hidden")} ref={childrenRef}>
          <div className={cn("w-[1px] self-stretch shrink-0", sidebarColor === "light" ? "bg-slate-200" : "bg-gray-50/50")} />
          <div className="w-full">
            {children}
          </div>
        </div>
      }
    </div>
  );
};
